import { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_GAP_PX = 24;

export function useHorizontalScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(maxScroll > 1 && el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    sync();
    el.addEventListener("scroll", sync, { passive: true });

    const observer = new ResizeObserver(sync);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, [sync]);

  const scroll = (direction: "left" | "right") => {
    const el = ref.current;
    if (!el) return;

    const firstChild = el.firstElementChild as HTMLElement | null;
    const step = firstChild
      ? firstChild.offsetWidth + SCROLL_GAP_PX
      : el.clientWidth * 0.8;

    el.scrollBy({
      left: direction === "right" ? step : -step,
      behavior: "smooth",
    });
  };

  return { ref, scroll, canScrollLeft, canScrollRight };
}

export default useHorizontalScroll;
